
import { Component, Prop, Ref, Vue } from "vue-property-decorator";

@Component
export default class ViewportFrameAnimation extends Vue {
  @Prop({ required: true, type: String }) private framePath!: string;
  @Prop({ required: true, type: String }) private posterSrc!: string;
  @Prop({ required: true, type: String }) private alt!: string;
  @Prop({ required: true, type: Number }) private frameCount!: number;
  @Prop({ required: false, type: Number, default: 25 })
  private frameRate!: number;

  @Ref() private readonly image!: HTMLImageElement;

  private source = "";
  private frameObjectUrls: string[] = [];
  private preloadObserver: IntersectionObserver | null = null;
  private playbackObserver: IntersectionObserver | null = null;
  private frameRequest: number | null = null;
  private loading = false;
  private framesReady = false;
  private playbackRequested = false;
  private hasPlayed = false;
  private componentDestroyed = false;

  private created() {
    this.source = this.posterSrc;
  }

  private mounted() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.source = this.frameUrl(this.frameCount - 1);
      return;
    }

    if (!("IntersectionObserver" in window)) {
      this.playbackRequested = true;
      this.preloadFrames();
      return;
    }

    this.preloadObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.preloadObserver?.disconnect();
        this.preloadObserver = null;
        this.preloadFrames();
      },
      { rootMargin: "100% 0px" }
    );
    this.preloadObserver.observe(this.image);

    this.playbackObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.playbackRequested = true;
        this.playbackObserver?.disconnect();
        this.playbackObserver = null;
        this.startPlayback();
      },
      { threshold: 0.01 }
    );
    this.playbackObserver.observe(this.image);
  }

  private frameUrl(index: number) {
    const base = process.env.BASE_URL || "/";
    const frame = String(index).padStart(5, "0");
    return `${base}title-frames/${this.framePath}/${frame}.webp`;
  }

  private async preloadFrames() {
    if (this.loading || this.framesReady) return;
    this.loading = true;
    this.frameObjectUrls = new Array(this.frameCount);
    let nextFrame = 0;

    const loadNext = async () => {
      while (nextFrame < this.frameCount && !this.componentDestroyed) {
        const index = nextFrame++;
        const response = await fetch(this.frameUrl(index));
        if (!response.ok) throw new Error(`Unable to load frame ${index}`);
        const blob = await response.blob();
        if (!this.componentDestroyed) {
          this.frameObjectUrls[index] = URL.createObjectURL(blob);
        }
      }
    };

    try {
      const concurrency = Math.min(8, this.frameCount);
      await Promise.all(Array.from({ length: concurrency }, loadNext));
      if (this.componentDestroyed) return;
      this.framesReady = true;
      this.startPlayback();
    } catch {
      // Leave the static logo visible if the sequence cannot be downloaded.
    }
  }

  private startPlayback() {
    if (!this.framesReady || !this.playbackRequested || this.hasPlayed) return;
    this.hasPlayed = true;
    const startedAt = performance.now();

    const renderFrame = (now: number) => {
      const elapsed = (now - startedAt) / 1000;
      const frame = Math.min(
        Math.floor(elapsed * this.frameRate),
        this.frameCount - 1
      );
      this.source = this.frameObjectUrls[frame];

      if (frame < this.frameCount - 1) {
        this.frameRequest = window.requestAnimationFrame(renderFrame);
      } else {
        this.frameRequest = null;
      }
    };

    this.frameRequest = window.requestAnimationFrame(renderFrame);
  }

  private destroyed() {
    this.componentDestroyed = true;
    this.preloadObserver?.disconnect();
    this.playbackObserver?.disconnect();
    if (this.frameRequest !== null) {
      window.cancelAnimationFrame(this.frameRequest);
    }
    this.frameObjectUrls.forEach((url) => {
      if (url) URL.revokeObjectURL(url);
    });
  }
}
